import React from 'react';
import { Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface Props {
  deadlineAt?: string | null;
  deliveredAt?: string | null;
  deadlineMissed?: boolean;
  className?: string;
}

/**
 * Compact deadline indicator for editor's per-task view.
 * Color rules (per spec 2026-05-03):
 *   green  → delivered on time (delivered_at set, deadline not missed)
 *   green  → > 4 hours away
 *   amber  → 1–4 hours away
 *   red    → < 1 hour away or missed (deadline_missed = true OR deadline < now & not delivered)
 *   grey   → no deadline set
 */
export function DeadlineChip({ deadlineAt, deliveredAt, deadlineMissed, className }: Props) {
  if (!deadlineAt) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 ${className || ''}`}>
        <Clock size={10} /> No deadline
      </span>
    );
  }

  const deadline = new Date(deadlineAt);
  const now = new Date();
  const msLeft = deadline.getTime() - now.getTime();
  const hoursLeft = msLeft / (60 * 60 * 1000);

  // Editor's local short format
  const fmt = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit', minute: '2-digit',
    day: 'numeric', month: 'short',
  });
  const label = fmt.format(deadline);

  // Delivered on time
  if (deliveredAt && !deadlineMissed) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 ${className || ''}`}>
        <CheckCircle2 size={10} /> Delivered
      </span>
    );
  }

  // Missed
  if (deadlineMissed || msLeft < 0) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 ${className || ''}`}>
        <AlertTriangle size={10} /> MISSED · {label}
      </span>
    );
  }

  // Approaching
  if (hoursLeft < 1) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 ${className || ''}`}>
        <Clock size={10} /> &lt; 1h · {label}
      </span>
    );
  }
  if (hoursLeft < 4) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 ${className || ''}`}>
        <Clock size={10} /> {Math.round(hoursLeft)}h · {label}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 ${className || ''}`}>
      <Clock size={10} /> Due {label}
    </span>
  );
}
